<template>
  <div
    v-if="enabled && videoUrl && text"
    class="video-banner"
    :style="{
      '--video-banner-background': textBackgroundColor,
    }"
  >
    <button
      class="video-banner__trigger"
      type="button"
      @click="openVideo"
    >
      <icon-play />
      <span
        class="text-body-s-bold"
        v-html="text"
      />
    </button>

    <teleport to="body">
      <overlay
        v-if="isVideoAvailable"
        block-class="video-banner-modal"
        direction="up"
        ignore-dismissed
        :namespace="namespace"
        :template="false"
        type="modal"
      >
        <template #body="overlay">
          <div
            class="video-banner__modal"
            :style="modalStyles"
            js-overlay="scrollElement"
          >
            <button
              class="video-banner__close icon-button"
              type="button"
              @click="overlay.close('button')"
            >
              <span
                class="visually-hidden"
                v-text="$string('accessibility.close')"
              />
              <icon-close />
            </button>

            <template v-if="overlay.isActive">
              <iframe
                v-if="externalVideoUrl"
                class="video-banner__iframe"
                :src="externalVideoUrl"
                :title="$string('accessibility.video.play')"
                allow="autoplay; fullscreen"
                allowfullscreen
              />

              <video
                v-else
                class="video-banner__video"
                :src="videoUrl"
                autoplay
                controls
                playsinline
              >
                <track
                  v-if="subtitles"
                  kind="captions"
                  :label="$variable('store.languageName')"
                  :src="subtitles"
                  :srclang="$variable('store.language')"
                >
              </video>
            </template>
          </div>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>

/**
 * Vue: Video banner (video-banner)
 * -----------------------------------------------------------------------------
 * Reusable video trigger and modal for direct, YouTube, and Vimeo URLs.
 *
 * @param {String} [backgroundColor] - Modal background colour.
 * @param {Boolean} [enabled] - Whether the trigger is shown.
 * @param {String} [hoverBackgroundColor] - Modal hover background colour.
 * @param {String} [namespace] - Unique overlay namespace.
 * @param {String} [text] - Trigger text.
 * @param {String} [videoUrl] - Direct, YouTube, or Vimeo video URL.
 * @param {String} [subtitles] - Video subtitles URL.
 * @param {Number} [width] - Modal video width as a percentage.
 */
import { mapActions } from 'vuex'

import IconClose from '~icons/directional-navigation/close.svg'
import IconPlay from '~icons/general/play.svg'
import Overlay from '~global/overlay/overlay'

export default {
  name: 'VideoBanner',

  components: {
    IconClose,
    IconPlay,
    Overlay,
  },

  props: {
    backgroundColor: {
      type: String,
      default: '',
    },
    enabled: {
      type: Boolean,
      default: false,
    },
    hoverBackgroundColor: {
      type: String,
      default: '',
    },
    namespace: {
      type: String,
      default: 'videoBanner',
    },
    text: {
      type: String,
      default: '',
    },
    subtitles: {
      type: String,
      default: '',
    },
    textBackgroundColor: {
      type: String,
      default: '',
    },
    videoUrl: {
      type: String,
      default: '',
    },
    width: {
      type: [Number, String],
      default: 100,
    },
  },

  computed: {

    /**
     * Compute modal styles from the section settings.
     * @returns {Object}
     */
    modalStyles() {
      return {
        '--video-modal-background': this.backgroundColor,
        '--video-modal-hover-background': this.hoverBackgroundColor,
        '--video-modal-width': `${this.width || 100}%`,
      }
    },

    /**
     * Compute the external video embed URL.
     * @returns {String}
     */
    externalVideoUrl() {
      try {
        const url = new URL(this.videoUrl)
        const host = url.hostname.replace('www.', '')

        if (
          host === 'youtube.com' ||
          host === 'youtube-nocookie.com' ||
          host === 'youtu.be'
        ) {
          const videoId = host === 'youtu.be'
            ? url.pathname.slice(1)
            : url.searchParams.get('v') || url.pathname.split('/').pop()

          return videoId
            ? `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`
            : ''
        }

        if (host === 'vimeo.com' || host === 'player.vimeo.com') {
          const videoId = url.pathname.split('/').filter(Boolean).pop()
          return videoId ? `https://player.vimeo.com/video/${videoId}?autoplay=1` : ''
        }
      } catch (error) {
        return ''
      }

      return ''
    },

    /**
     * Compute whether the configured video can be opened.
     * @returns {Boolean}
     */
    isVideoAvailable() {
      return Boolean(this.externalVideoUrl || this.videoUrl)
    },
  },

  methods: {
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    openVideo() {
      this.openOverlay({
        ignoreDismissed: true,
        namespace: this.namespace,
      })
    },
  },
}
</script>

<style lang="scss">
@import './video-banner';
</style>
